









import Vue from "vue";
import { CaraSDK } from "@cara-web-sdk";
import { isAustralianApiUrl } from "@journeys/utils/helpers";
import { getCaraAccessToken } from "@/services/cara";
import { setPersonAttributes, trackEvent } from "@/utils/analytics/analytics";
import { isEmbedded } from "@/utils/embed/embed";
import { notifyPartner } from "@/utils/embed/partnerEmbed";

// The SDK routes in root-relative paths (/onboarding, /chat, /goals, /exercises/:id)
// but is embedded under /cara here, so translate between the two on the way in and out.
const CARA_BASE = "/cara";

const toSdkPathname = (appPath: string): string => {
  const stripped = appPath.slice(CARA_BASE.length);
  return stripped.startsWith("/") ? stripped : "/";
};

const toAppPath = (sdkPathname: string): string =>
  sdkPathname === "/" ? CARA_BASE : `${CARA_BASE}${sdkPathname}`;

// Web counterpart of react-native-prototype's src/pages/cara/Handover.tsx: gates on
// the `cara_ai` feature flag (as the old marketing page did), fetches a Cara token,
// then mounts the vendored Cara Web SDK in place of the app-store download page.
export default Vue.extend({
  name: "CaraWeb",
  data() {
    return {
      loading: true,
    };
  },
  async mounted() {
    const flagResponse = await this.api({
      path: "api/u/feature-flag",
      feature_flags: ["cara_ai"],
    });
    if (!flagResponse?.feature_flags?.cara_ai) {
      this.$router.push("/404");
      return;
    }

    const email = this.$store.getters.user.user.email;
    const token = this.$store.getters.user_id;
    if (!token) {
      this.$router.push("/404");
      return;
    }

    this.loading = false;
    await this.$nextTick();

    // Region is inferred from the API origin, matching the rest of the app
    // (see supportEmail, journey DMH validation). AU tenants point at an AU API.
    const region = isAustralianApiUrl(process.env.VUE_APP_API_PATH) ? "AU" : "IE";

    // Zurich gets the SDK's blue palette; every other tenant keeps the default purple.
    const theme = this.$store.getters.subdomain === "zurich" ? "blue" : "purple";

    // Non-reactive: the SDK instance holds DOM nodes and a Redux store that must not
    // be walked by Vue's reactivity system, so it is not declared in data().
    (this as any).caraInstance = CaraSDK.init({
      container: this.$refs.caraRoot as HTMLElement,
      token,
      appVersion: "wellness",
      apiBaseUrl: process.env.VUE_APP_CARA_API_URL || undefined,
      region,
      theme,
      pathname: toSdkPathname(this.$route.path),
      // SDK screen change -> reflect it in the browser URL (under /cara). push() so each
      // Cara screen builds up in the back-stack; the $route watcher below feeds real
      // back/forward/refresh navigations the other way.
      onNavigate: (pathname: string) => {
        const target = toAppPath(pathname);
        if (this.$route.path === target) {
          return;
        }
        // Replace (don't add history) for the landing redirect off the bare /cara base,
        // and for SDK redirects triggered while syncing a browser back/forward — pushing
        // those would trap the back button on a disallowed route.
        const shouldReplace =
          this.$route.path === CARA_BASE || (this as any).syncingFromApp;
        (this as any).syncingFromSdk = true;
        this.$router[shouldReplace ? "replace" : "push"](target)
          .catch(() => { })
          .then(() => {
            (this as any).syncingFromSdk = false;
          });
      },
      onClose: () => {
        this.$router.push("/");
      },
      // Fired after the member deletes their Cara data from the SDK's preferences.
      onLogout: () => {
        this.$store.dispatch("logout");
        if (isEmbedded()) {
          notifyPartner({ type: "sl:close", v: 1, reason: "session_expired" });
          return;
        }
        this.$router.push("/login").catch(() => { });
      },
      onBookDMHSession: () => {
        this.$router.push("/session-booking?service_type=sl_dmh");
      },
      // The SDK owns the cara_* event names and fire points (shared with mobile);
      // the host only supplies the PostHog sink and its identity super-properties.
      trackEvent,
      setPersonAttributes,
    });
  },
  watch: {
    // Browser back/forward/hard-refresh within /cara -> drive the SDK to that screen.
    // Skipped while the change originated from the SDK's own onNavigate (see above).
    // syncingFromApp marks any onNavigate the SDK re-emits during this update as a
    // redirect, so it replaces rather than pushes.
    "$route.path"(newPath: string) {
      const instance = (this as any).caraInstance;
      if (!instance || (this as any).syncingFromSdk || !newPath.startsWith(CARA_BASE)) {
        return;
      }
      (this as any).syncingFromApp = true;
      try {
        instance.updatePathname(toSdkPathname(newPath));
      } finally {
        (this as any).syncingFromApp = false;
      }
    },
  },
  beforeDestroy() {
    const instance = (this as any).caraInstance;
    if (instance) {
      instance.destroy();
    }
  },
});
